const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

export const INLINE_MARKDOWN_TOKEN_PATTERN: RegExp = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`)/g;

export const isSafeMarkdownLinkHref = (href: string): boolean => {
    const trimmed: string = href.trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('#')) return true;
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
    if (trimmed.startsWith('//')) return false;
    if (!trimmed.includes(':')) return true;

    const scheme: string = trimmed.split(':', 1)[0].toLowerCase();
    return scheme === 'http' || scheme === 'https' || scheme === 'mailto';
};

export type InlineMarkdownPart =
    | { type: 'text'; text: string }
    | { type: 'link'; label: string; href: string; safe: boolean }
    | { type: 'strong'; children: InlineMarkdownPart[] }
    | { type: 'emphasis'; children: InlineMarkdownPart[] }
    | { type: 'code'; text: string };

export const parseInlineMarkdown = (text: string): InlineMarkdownPart[] =>
    text
        .split(INLINE_MARKDOWN_TOKEN_PATTERN)
        .filter((part: string) => part.length > 0)
        .map((part: string): InlineMarkdownPart => {
            const linkMatch: RegExpMatchArray | null = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
            if (linkMatch) {
                const href: string = linkMatch[2];
                return { type: 'link', label: linkMatch[1], href, safe: isSafeMarkdownLinkHref(href) };
            }

            if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
                return { type: 'strong', children: parseInlineMarkdown(part.slice(2, -2)) };
            }

            if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**') && part.length >= 2) {
                return { type: 'emphasis', children: parseInlineMarkdown(part.slice(1, -1)) };
            }

            if (part.startsWith('_') && part.endsWith('_') && part.length >= 2) {
                return { type: 'emphasis', children: parseInlineMarkdown(part.slice(1, -1)) };
            }

            if (part.startsWith('`') && part.endsWith('`')) {
                return { type: 'code', text: part.slice(1, -1) };
            }

            return { type: 'text', text: part };
        });

const formatInlinePartsToHtml = (parts: InlineMarkdownPart[]): string =>
    parts
        .map((part: InlineMarkdownPart): string => {
            switch (part.type) {
                case 'text':
                    return escapeHtml(part.text);
                case 'link':
                    return part.safe
                        ? `<a href="${escapeHtml(part.href)}">${escapeHtml(part.label)}</a>`
                        : escapeHtml(part.label);
                case 'strong':
                    return `<strong>${formatInlinePartsToHtml(part.children)}</strong>`;
                case 'emphasis':
                    return `<em>${formatInlinePartsToHtml(part.children)}</em>`;
                case 'code':
                    return `<code>${escapeHtml(part.text)}</code>`;
            }
        })
        .join('');

export const formatInlineMarkdownToHtml = (text: string): string => formatInlinePartsToHtml(parseInlineMarkdown(text));
